/**
 * Data Source Context
 * 
 * Manages data source configuration state across the application.
 * Provides CRUD operations for data sources.
 * 
 * STORAGE STRATEGY:
 * - Initializes from demoDataSourceConfig.ts (in git repository)
 * - Changes persist in React state during session
 * - Changes reset on page refresh (back to demo config)
 * - This ensures consistent demo experience for all users
 * 
 * BEHAVIOR:
 * - On mount: Load 6 pre-configured demo sources
 * - During session: Users can add/edit/remove sources
 * - On refresh: Resets to demo configuration
 * 
 * USE CASE:
 * - Sales demos start with consistent state
 * - Training sessions have predictable setup
 * - Customers see same demo environment
 * - Developers share configuration via git
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { DataSourceConfig, DataSourceMappingConfig } from '@/types/dataSource';
import { getDemoDataSources } from '@/data/demoDataSourceConfig';

interface DataSourceContextType {
  dataSources: DataSourceConfig[];
  addDataSource: (dataSource: Omit<DataSourceConfig, 'id' | 'dateAdded'>) => DataSourceConfig;
  updateDataSource: (id: string, updates: Partial<DataSourceConfig>) => void;
  updateMappingConfig: (id: string, mappingConfig: DataSourceMappingConfig) => void;
  removeDataSource: (id: string) => void;
  getDataSource: (id: string) => DataSourceConfig | undefined;
  refreshDataSources: () => void;
  isLoading: boolean;
}

const DataSourceContext = createContext<DataSourceContextType | undefined>(undefined);

interface DataSourceProviderProps {
  children: ReactNode;
}

export const DataSourceProvider: React.FC<DataSourceProviderProps> = ({ children }) => {
  // Initialize from demo configuration (always starts with 6 pre-configured sources)
  const [dataSources, setDataSources] = useState<DataSourceConfig[]>(getDemoDataSources());
  const [isLoading] = useState(false); // No async loading needed for demo config

  /**
   * Add a new data source
   * 
   * Creates a new data source with generated ID and timestamp.
   * Changes persist in React state during session only.
   * On refresh, new sources are lost (resets to demo config).
   */
  const addDataSource = (dataSource: Omit<DataSourceConfig, 'id' | 'dateAdded'>): DataSourceConfig => {
    const newDataSource: DataSourceConfig = {
      ...dataSource,
      id: crypto.randomUUID(),
      dateAdded: new Date().toISOString(),
    };
    
    setDataSources((prev) => [...prev, newDataSource]);
    return newDataSource;
  };

  /**
   * Update an existing data source
   * 
   * Updates data source properties by ID.
   * Changes persist in React state during session only.
   * On refresh, updates are lost (resets to demo config).
   */
  const updateDataSource = (id: string, updates: Partial<DataSourceConfig>): void => {
    setDataSources((prev) =>
      prev.map((ds) => (ds.id === id ? { ...ds, ...updates } : ds))
    );
  };

  /**
   * Remove a data source
   * 
   * Removes data source by ID.
   * Changes persist in React state during session only.
   * On refresh, removed sources reappear if they were in demo config.
   */
  const removeDataSource = (id: string): void => {
    setDataSources((prev) => prev.filter((ds) => ds.id !== id));
  };

  /**
   * Get a single data source by ID
   */
  const getDataSource = (id: string): DataSourceConfig | undefined => {
    return dataSources.find((ds) => ds.id === id);
  };

  /**
   * Update the mapping layer configuration for a data source.
   * Persists in React state during session only.
   */
  const updateMappingConfig = (id: string, mappingConfig: DataSourceMappingConfig): void => {
    setDataSources((prev) =>
      prev.map((ds) => (ds.id === id ? { ...ds, mappingConfig } : ds))
    );
  };



  /**
   * Refresh data sources from demo configuration
   * 
   * Resets state back to the 6 pre-configured demo sources.
   * Useful for "reset to default" functionality.
   */
  const refreshDataSources = (): void => {
    setDataSources(getDemoDataSources());
  };

  const value: DataSourceContextType = {
    dataSources,
    addDataSource,
    updateDataSource,
    updateMappingConfig,
    removeDataSource,
    getDataSource,
    refreshDataSources,
    isLoading,
  };

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
};

/**
 * Hook to use DataSourceContext
 * 
 * Must be used within a DataSourceProvider.
 * Provides access to data source state and CRUD operations.
 */
export const useDataSource = (): DataSourceContextType => {
  const context = useContext(DataSourceContext);
  if (!context) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
};

/**
 * Alias for useDataSource
 * Provides the same functionality with a more descriptive name
 */
export const useDataSourceContext = useDataSource;
